import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TimeoutModal from './TimeoutModal';
import SessionTimeoutConfig from '../../config/sessionConfig';
import './SessionTimer.css';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];
const ACTIVITY_THROTTLE_MS = 5000; // reset at most once every 5 seconds

const SessionTimer = () => {
  const { logout } = useAuth();
  const [countdown, setCountdown] = useState(SessionTimeoutConfig.SESSION_COUNTDOWN_START);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [modalCountdown, setModalCountdown] = useState(SessionTimeoutConfig.AUTO_LOGOUT_WAIT);

  const countdownTimerRef = useRef(null);
  const modalTimerRef = useRef(null);
  const modalCountdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  // Ref so activity handler always reads current modal state without re-registering
  const showTimeoutModalRef = useRef(false);

  useEffect(() => {
    showTimeoutModalRef.current = showTimeoutModal;
  }, [showTimeoutModal]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    setCountdown(SessionTimeoutConfig.SESSION_COUNTDOWN_START);
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          setShowTimeoutModal(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Throttled activity handler — resets the inactivity countdown on user interaction.
  // Does nothing if the warning modal is already visible (user must respond to it).
  const handleActivity = useCallback(() => {
    if (showTimeoutModalRef.current) return;
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;
    startCountdown();
  }, [startCountdown]);

  // Register activity listeners
  useEffect(() => {
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [handleActivity]);

  const handleStayInSession = useCallback(() => {
    setShowTimeoutModal(false);
    setModalCountdown(SessionTimeoutConfig.AUTO_LOGOUT_WAIT);
    if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
    if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
    lastActivityRef.current = Date.now();
    startCountdown();
  }, [startCountdown]);

  const handleLogout = useCallback(() => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
    if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
    logout();
  }, [logout]);

  // Start timer on mount, clean up on unmount
  useEffect(() => {
    startCountdown();
    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
      if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
    };
  }, [startCountdown]);

  // Auto-logout countdown once warning modal is shown
  useEffect(() => {
    if (!showTimeoutModal) return;

    setModalCountdown(SessionTimeoutConfig.AUTO_LOGOUT_WAIT);

    modalCountdownRef.current = setInterval(() => {
      setModalCountdown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    modalTimerRef.current = setTimeout(() => {
      handleLogout();
    }, SessionTimeoutConfig.AUTO_LOGOUT_WAIT * 1000);

    return () => {
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
      if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
    };
  }, [showTimeoutModal, handleLogout]);

  return (
    <>
      <div className="session-timer">
        <span className="session-timer-label">Session</span>
        <span className="session-timer-divider">|</span>
        <span className="session-timer-value">{formatTime(countdown)}</span>
      </div>

      {showTimeoutModal && (
        <TimeoutModal
          countdown={modalCountdown}
          onStayInSession={handleStayInSession}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};

export default SessionTimer;
